import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Icon,
    FormControlLabel,
    Checkbox,
    Radio,
    FormHelperText,
    CircularProgress,
    Alert,
} from "@mui/material";
import { CURRENCY, ModifierGroup, Product } from "../../types/pos.types";
import { menuItemsService } from "../../../business-admin/services/menu-items.service";

interface Props {
    item: Product | null;
    open: boolean;
    onClose: () => void;
    onAdd: (itemWithModifiers: Product & {
        quantity: number;
        modifiers: Record<string, string | string[]>;
    }) => void;
}


export const ItemDetailsModal: React.FC<Props> = ({ item, open, onClose, onAdd }) => {
    const [quantity, setQuantity] = useState(1);
    const [selectedModifiers, setSelectedModifiers] = useState<Record<string, string | string[]>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [modifiers, setModifiers] = useState<ModifierGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch modifiers when modal opens and item is available
    useEffect(() => {
        if (!open || !item) {
            // Reset state
            setQuantity(1);
            setSelectedModifiers({});
            setErrors({});
            setModifiers([]);
            setLoading(false);
            setError(null);
            return;
        }

        const loadModifiers = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await menuItemsService.getMenuItem(item.id || item._id);
                if (response.success && response.data?.modifiers) {
                    const normalizedModifiers = response.data.modifiers.map(group => ({
                        id: group.id,
                        name: group.name,
                        type: group.type,
                        required: Boolean(group.required),
                        min_selection: group.min_selection ?? 0,
                        max_selection: group.max_selection ?? 1,
                        modifiers: group.modifiers || [],
                    }));
                    setModifiers(normalizedModifiers);
                }
            } catch (err) {
                console.error("Failed to fetch modifiers:", err);
                setError("Could not load modifiers. Please try again later.");
                setModifiers([]);
            } finally {
                setLoading(false);
            }
        };

        loadModifiers();
    }, [open, item]);

    // Auto-select required radio modifiers when modifiers change
    useEffect(() => {
        if (!item || loading) return;

        const newSelections: Record<string, string | string[]> = {};
        modifiers.forEach(group => {
            if (
                group.type === "radio" &&
                group.required &&
                !selectedModifiers[group.id]
            ) {
                const firstModifier = group.modifiers[0];
                if (firstModifier) {
                    newSelections[group.id] = firstModifier.id;
                }
            }
        });

        if (Object.keys(newSelections).length > 0) {
            setSelectedModifiers(prev => ({
                ...prev,
                ...newSelections,
            }));
        }
    }, [modifiers, loading]);

    const handleIncrease = () => {
        if (quantity < 20) setQuantity(quantity + 1);
    };

    const handleDecrease = () => {
        if (quantity > 1) setQuantity(quantity - 1);
    };

    const validateModifiers = () => {
        const newErrors: Record<string, string> = {};
        modifiers.forEach(group => {
            const selected = selectedModifiers[group.id];

            if (group.required || (group.min_selection && group.min_selection > 0)) {
                if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                    newErrors[group.id] = "Please make a selection.";
                } else if (Array.isArray(selected)) {
                    const count = selected.length;
                    if (group.min_selection && count < group.min_selection) {
                        newErrors[group.id] = `At least ${group.min_selection} required.`;
                    }
                    if (group.max_selection && count > group.max_selection) {
                        newErrors[group.id] = `Maximum ${group.max_selection} allowed.`;
                    }
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateModifiers()) return;

        const finalPrice = calculateTotalPrice();

        const itemWithModifiers = {
            ...item!,
            quantity,
            price: finalPrice,
            modifiers: selectedModifiers,
        };

        onAdd(itemWithModifiers);

        // Reset form
        setQuantity(1);
        setSelectedModifiers({});
        setErrors({});
        onClose();
    };

    // Recalculate total price whenever modifiers or base price changes
    const calculateTotalPrice = () => {
        let totalPrice = item?.basePrice || item?.price || 0;
        let modifierSum = 0;

        Object.entries(selectedModifiers).forEach(([groupId, selected]) => {
            const group = modifiers.find(g => g.id === groupId);
            if (!group) return;

            if (group.type === "radio" && typeof selected === "string") {
                const mod = group.modifiers.find(m => m.id === selected);
                if (mod) modifierSum += mod.price;
            } else if (group.type === "checkbox" && Array.isArray(selected)) {
                selected.forEach(id => {
                    const mod = group.modifiers.find(m => m.id === id);
                    if (mod) modifierSum += mod.price;
                });
            }
        });

        return (totalPrice + modifierSum) * quantity;
    };

    if (!item) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{item.name}</DialogTitle>
            <DialogContent>
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    mb: 2,
                    width: "100%",
                }}>
                    <img
                        src="/image.png"
                        alt={item.name}
                        style={{
                            width: "50%",
                            maxHeight: 200,
                            objectFit: "cover",
                            borderRadius: 4,
                        }}
                    />
                </Box>

                {/* Quantity Selector */}
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, my: 3 }}>
                    <IconButton onClick={handleDecrease}>
                        <Icon>remove</Icon>
                    </IconButton>
                    <TextField
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= 20) {
                                setQuantity(val);
                            }
                        }}
                        inputProps={{ min: 1, max: 20, style: { textAlign: "center", width: 60 } }}
                        size="small"
                    />
                    <IconButton onClick={handleIncrease}>
                        <Icon>add</Icon>
                    </IconButton>
                </Box>

                {/* Loader / Error */}
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Modifier Groups */}
                {!loading && modifiers.length > 0 ? (
                    modifiers.map((group) => (
                        <Box key={group.id} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                {group.name} {group.required ? "(Required)" : ""}
                            </Typography>
                            {group.type === "radio" ? (
                                group.modifiers.map((mod) => (
                                    <FormControlLabel
                                        key={mod.id}
                                        control={
                                            <Radio
                                                checked={(selectedModifiers[group.id] as string) === mod.id}
                                                onChange={() =>
                                                    setSelectedModifiers({
                                                        ...selectedModifiers,
                                                        [group.id]: mod.id,
                                                    })
                                                }
                                            />
                                        }
                                        label={`${mod.name} (+${CURRENCY} ${mod.price.toFixed(2)})`}
                                        sx={{ display: "block" }}
                                    />
                                ))
                            ) : (
                                group.modifiers.map((mod) => (
                                    <FormControlLabel
                                        key={mod.id}
                                        control={
                                            <Checkbox
                                                checked={(selectedModifiers[group.id] as string[])?.includes(mod.id)}
                                                onChange={(e) => {
                                                    const current = (selectedModifiers[group.id] as string[]) || [];
                                                    const newSelection = e.target.checked
                                                        ? [...current, mod.id]
                                                        : current.filter((id) => id !== mod.id);
                                                    setSelectedModifiers({
                                                        ...selectedModifiers,
                                                        [group.id]: newSelection,
                                                    });
                                                }}
                                            />
                                        }
                                        label={`${mod.name} (+${CURRENCY} ${mod.price.toFixed(2)})`}
                                        sx={{ display: "block" }}
                                    />
                                ))
                            )}
                            {errors[group.id] && (
                                <FormHelperText error>{errors[group.id]}</FormHelperText>
                            )}
                        </Box>
                    ))
                ) : (
                    !loading && (
                        <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                            No modifiers available for this item.
                        </Typography>
                    )
                )}

                <Typography variant="h6" align="center">
                    Total Price: {CURRENCY} {(calculateTotalPrice()).toFixed(2)}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
                <Button
                    onClick={onClose}
                    startIcon={<Icon>close</Icon>}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0 || loading}
                    sx={{
                        bgcolor: "#0891B2",
                        "&:hover": {
                            bgcolor: "#0E7490",
                        },
                    }}
                >
                    Add to Cart
                </Button>
            </DialogActions>
        </Dialog>
    );
};